import axiosConfig from "./axiosConfig";
import { LeadCandidate } from "./postLeadSearch";

// 202 kickoff response: the retry runs as a long-running operation, so the caller
// polls /operations/<operationKey> and reads the refreshed rows out of its `result`.
export interface VoaRefetchDegradedResponse {
  status: string;
  operationKey: string;
  siteId: string;
  rowCount: number;
  // Rows the request carried beyond what one operation takes on. Capped rather than
  // rejected (the caller retries every flagged row on the page and cannot offer a
  // smaller selection), so a non-zero overflow must be surfaced as "click again for
  // the rest" - never dropped silently.
  overflowCount: number;
  // True when this key is an ALREADY-RUNNING identical retry the BE coalesced onto.
  deduped?: boolean;
}

// The operation `result` written by the BE worker (operation_voa_refetch_degraded).
// `candidates` covers every requested ref (refreshed), so the caller can merge them
// into its result rows; the ref lists explain what happened to each row.
export interface VoaRefetchDegradedOperationResult {
  status: string;
  total: number;
  candidates: LeadCandidate[];
  refetched: string[];
  notDegraded: string[];
  unknown: string[];
  stillDegraded: string[];
}

// Bulk variant of postVoaRefetch: takes a list of source refs and on the BE clears
// only the caches/timestamps tied to each row's `enrichmentDegradedSources`. Use this
// when a search produced rows flagged with missing data (upstream rate-limited/timed
// out) and the operator wants to retry just those sources without paying for a full
// per-row refetch.
//
// Asynchronous: the work is upstream-bound (one Overpass call per row, Companies House
// rate-limited per postcode), and as a synchronous endpoint it was timing out at the
// 29s API Gateway ceiling on a single row.
const postVoaRefetchDegraded = async (
  authToken: string,
  sourceRefs: string[]
): Promise<VoaRefetchDegradedResponse> => {
  const { data } = await axiosConfig.post(
    "/leads/voa-refetch-degraded",
    { sourceRefs },
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

export default postVoaRefetchDegraded;
